import { useCallback, useRef } from 'react';
import pickBy from 'lodash/pickBy';
import {
	useRouterActions,
	type Query,
	type MatchParams,
	type Route,
} from '@atlassian/react-resource-router';

type UpdatableQuery = {
	[key: string]: string | undefined;
};

type HistoryAttributes = { query?: UpdatableQuery; params?: MatchParams };

export type PushOrReplace = (attributes: HistoryAttributes, shouldReplace?: boolean) => void;

export const useRoutePushOrReplace = (
	query: Query,
	params: MatchParams,
	route: Route,
): PushOrReplace => {
	const { pushTo, replaceTo } = useRouterActions();
	const pushOrReplace = useCallback(
		({ query: newQuery, params: newParams }: HistoryAttributes, shouldReplace = false) => {
			// Omit any query params that have explicitly been set to undefined
			const updatedQuery = pickBy(
				{
					...query,
					...newQuery,
				},
				(param) => param != null,
			);
			const historyAction = shouldReplace ? replaceTo : pushTo;
			historyAction(route, {
				query: updatedQuery,
				params: {
					...params,
					...newParams,
				},
			});
		},
		[params, query, pushTo, replaceTo, route],
	);
	const pushOrReplaceStable = useRef(pushOrReplace);
	pushOrReplaceStable.current = pushOrReplace;

	// We ensure this hook returns a stable function reference to avoid unnecessary re-renders when params change.
	return useCallback(
		(attributes, shouldReplace) => pushOrReplaceStable.current(attributes, shouldReplace),
		[],
	);
};
